// Site-wide search index — all content from Sanity → src/cms/*.json

import { blocksToPlainText } from '../../utils/portableTextUtils';
import staffMembers from '../../cms/staff.json';
import boardMembers from '../../cms/board.json';
import studentGroups from '../../cms/studentgroups.json';
import fellowships from '../../cms/fellowships.json';
import holidaysData from '../../cms/holidays.json';
import homepage from '../../cms/homepage.json';
import faqData from '../../cms/faq.json';
import roomReservation from '../../cms/room-reservation.json';
import religiousLife from '../../cms/religious-life.json';
import travelOpportunities from '../../cms/travel.json';
import opportunities from '../../cms/opportunities.json';
import kosher from '../../cms/kosher.json';
import hubs from '../../cms/hubs.json';
import alumniPage from '../../cms/alumni.json';
import sitePages from '../../cms/site-pages.json';
import searchSettings from '../../cms/search-settings.json';
import { currentEvents } from '../events/currentEvents/currentEventsIndex';

const RELIGIOUS_LIFE_SECTIONS = [
  'ou-jlic',
  'minyan',
  'eruv',
  'housing',
  'kosher-dining',
  'shabbat-meals',
];

/** Plain text for search (legacy HTML strings or portable text blocks). */
function plainTextForSearch(text) {
  if (typeof text === 'string') {
    return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  return blocksToPlainText(text);
}

function richTextForSearch(value) {
  if (typeof value === 'string') return value;
  return blocksToPlainText(value);
}

/** Join searchable fields, flattening portable text blocks. */
function searchContent(...parts) {
  return parts
    .filter((p) => p != null && p !== '')
    .map((p) => richTextForSearch(p))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Flatten custom-page builder sections into searchable text. */
function sectionsToPlainText(sections) {
  if (!Array.isArray(sections)) return '';
  const parts = [];
  for (const block of sections) {
    if (!block) continue;
    [block.heading, block.subheading, block.text, block.caption].forEach((t) => {
      if (t) parts.push(richTextForSearch(t));
    });
    if (block.content) parts.push(blocksToPlainText(block.content));
    (block.cards || []).forEach((c) => {
      if (c?.title) parts.push(c.title);
      if (c?.text) parts.push(richTextForSearch(c.text));
    });
    (block.members || []).forEach((m) => {
      if (m?.name) parts.push(m.name);
      if (m?.role) parts.push(m.role);
      if (m?.bio) parts.push(plainTextForSearch(m.bio));
    });
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

function opportunityTitle(item) {
  return item.title || item.name || '';
}

export const searchIndex = [
  // Staff
  ...staffMembers.map((member) => {
    const bioPlain = plainTextForSearch(member.bio);
    return {
      id: `staff-${member.id}`,
      type: 'staff',
      title: member.name,
      subtitle: member.title,
      description: bioPlain,
      content: `${member.name} ${member.title} ${bioPlain}`,
      path: `/staff/${member.id}`,
      icon: '👥',
      category: 'People',
      priority: 1,
    };
  }),

  // Board Members
  ...boardMembers.map((member) => {
    const bioPlain = plainTextForSearch(member.bio);
    return {
      id: `board-${member.id}`,
      type: 'board',
      title: member.name,
      subtitle: member.title,
      description: bioPlain,
      content: `${member.name} ${member.title} ${bioPlain}`,
      path: `/board/${member.id}`,
      icon: '🏛️',
      category: 'People',
      priority: 1,
    };
  }),

  // Student Groups
  ...studentGroups.map((group) => {
    const descPlain = richTextForSearch(group.description);
    return {
    id: `group-${group.id}`,
    type: 'student-group',
    title: group.name,
    subtitle: `Contact: ${group.contact?.name || ''}`,
    description: descPlain,
    content: searchContent(group.name, descPlain, group.contact?.name),
    path: '/student-groups',
    icon: '👥',
    category: 'Student Life',
    priority: 1,
  };
  }),

  // Fellowships
  ...fellowships.map((fellowship) => {
    const descPlain = richTextForSearch(fellowship.description);
    return {
    id: `fellowship-${fellowship.id}`,
    type: 'fellowship',
    title: fellowship.name,
    subtitle: `${fellowship.duration || ''} • Contact: ${fellowship.contact?.name || ''}`,
    description: descPlain,
    content: searchContent(fellowship.name, descPlain, fellowship.duration),
    path: '/jewish-learning',
    icon: '🎓',
    category: 'Jewish Learning',
    priority: 1,
  };
  }),

  // Holidays
  ...holidaysData.map((holiday) => {
    const descPlain = richTextForSearch(holiday.description) || 'Jewish holiday celebration';
    return {
    id: `holiday-${holiday.id}`,
    type: 'holiday',
    title: holiday.name,
    subtitle: holiday.generalDates || 'Check back for dates',
    description: descPlain,
    content: searchContent(holiday.name, descPlain, holiday.generalDates),
    path: '/holidays',
    icon: '⭐',
    category: 'Events',
    priority: 1,
  };
  }),

  // Homepage student types
  ...(homepage.studentTypes || []).map((studentType) => {
    const descPlain = richTextForSearch(studentType.description);
    return {
    id: `student-${studentType.id}`,
    type: 'student-type',
    title: studentType.title,
    subtitle: studentType.subtitle,
    description: descPlain,
    content: searchContent(studentType.title, studentType.subtitle, descPlain),
    path: '/student-life',
    icon: '🎓',
    category: 'Student Life',
    priority: 1,
  };
  }),

  // FAQ
  ...faqData.map((faq) => {
    const answerPlain = richTextForSearch(faq.answer);
    return {
    id: `faq-${faq.id}`,
    type: 'faq',
    title: faq.question,
    subtitle: 'Frequently Asked Question',
    description: answerPlain,
    content: `${faq.question} ${answerPlain}`,
    path: '/faq',
    icon: '❓',
    category: 'Information',
    priority: 2,
  };
  }),

  // Room Reservation
  {
    id: 'room-reservation',
    type: 'facility',
    title: roomReservation.info?.title || 'Reserve a Room',
    subtitle: roomReservation.info?.subtitle || '',
    description: richTextForSearch(roomReservation.info?.description),
    content: searchContent(
      roomReservation.info?.title,
      roomReservation.info?.subtitle,
      roomReservation.info?.description
    ),
    path: '/room-reservation',
    icon: '🏢',
    category: 'Facility',
    priority: 2,
  },

  // Religious Life topics
  ...RELIGIOUS_LIFE_SECTIONS.filter((key) => religiousLife[key]).map((key) => {
    const topic = religiousLife[key];
    const sectionSnippet = richTextForSearch(
      topic.sections?.[0]?.content || topic.description || ''
    );
    return {
      id: key,
      type: 'religious-life',
      title: topic.title,
      subtitle: topic.subtitle,
      description: sectionSnippet,
      content: searchContent(topic.title, topic.subtitle, sectionSnippet),
      path: `/religious-life?section=${key}`,
      icon: '✡️',
      category: 'Religious Life',
      priority: 1,
    };
  }),

  // Current Events
  ...currentEvents.map((event) => {
    const descPlain = richTextForSearch(event.description) || 'Hopkins Hillel event';
    return {
    id: `event-${event.id}`,
    type: 'event',
    title: event.title,
    subtitle: event.date || event.time || 'Check back for details',
    description: descPlain,
    content: searchContent(event.title, descPlain, event.date, event.time),
    path: '/events',
    icon: '📅',
    category: 'Events',
    priority: 1,
  };
  }),

  // Travel
  ...travelOpportunities.map((opportunity) => {
    const descPlain = richTextForSearch(opportunity.description);
    return {
    id: `travel-${opportunity.id}`,
    type: 'travel',
    title: opportunityTitle(opportunity),
    subtitle: opportunity.duration || opportunity.subtitle || '',
    description: descPlain,
    content: searchContent(opportunityTitle(opportunity), descPlain),
    path: '/travel-opportunities',
    icon: '✈️',
    category: 'Travel',
    priority: 2,
  };
  }),

  // Internships
  ...opportunities.map((opportunity) => {
    const descPlain = richTextForSearch(opportunity.description);
    return {
    id: `internship-${opportunity.id}`,
    type: 'internship',
    title: opportunityTitle(opportunity),
    subtitle: opportunity.duration || opportunity.subtitle || '',
    description: descPlain,
    content: searchContent(opportunityTitle(opportunity), descPlain),
    path: '/internship-opportunities',
    icon: '💼',
    category: 'Opportunities',
    priority: 2,
  };
  }),

  // Kosher Soup
  {
    id: 'kosher-soup',
    type: 'kosher-soup',
    title: kosher.title || 'Kosher Soup Delivery',
    subtitle: kosher.subtitle || '',
    description: richTextForSearch(kosher.intro),
    content: searchContent(kosher.title, kosher.subtitle, kosher.intro),
    path: '/kosher-soup-delivery',
    icon: '🥣',
    category: 'Services',
    priority: 2,
  },

  // Custom pages (Monica-created)
  ...sitePages
    .filter((p) => p.enabled && p.slug)
    .map((page) => {
      const sectionsPlain = sectionsToPlainText(page.sections);
      const bodyPlain = sectionsPlain || blocksToPlainText(page.body);
      return {
        id: `site-page-${page.slug}`,
        type: 'page',
        title: page.title,
        subtitle: page.subtitle || '',
        description: bodyPlain.slice(0, 200),
        content: `${page.title} ${page.subtitle || ''} ${bodyPlain}`,
        path: page.shortUrl ? `/${page.shortUrl}` : `/pages/${page.slug}`,
        icon: '📄',
        category: 'Information',
        priority: 2,
      };
    }),

  // Alumni page
  {
    id: 'alumni',
    type: 'page',
    title: alumniPage.title || 'Alumni',
    subtitle: alumniPage.subtitle || '',
    description: richTextForSearch(alumniPage.description),
    content: searchContent(alumniPage.title, alumniPage.subtitle, alumniPage.description),
    path: '/alumni',
    icon: '🎓',
    category: 'Alumni',
    priority: 2,
  },

  // Hub landing pages (from CMS)
  ...Object.entries(hubs).flatMap(([hubKey, hub]) => {
    const hubSubtitlePlain = richTextForSearch(hub.pageSubtitle);
    return [
    {
      id: `hub-${hubKey}`,
      type: 'page',
      title: hub.pageTitle,
      subtitle: hubSubtitlePlain,
      description: hubSubtitlePlain,
      content: searchContent(hub.pageTitle, hubSubtitlePlain),
      path: `/${hubKey === 'studentLife' ? 'student-life' : hubKey}`,
      icon: 'ℹ️',
      category: 'Information',
      priority: 2,
    },
    ...(hub.cards || []).map((card) => {
      const cardDescPlain = richTextForSearch(card.description);
      return {
      id: `hub-${hubKey}-card-${card.id}`,
      type: 'page',
      title: card.title,
      subtitle: card.subtitle,
      description: cardDescPlain,
      content: searchContent(card.title, card.subtitle, cardDescPlain),
      path: card.externalUrl || card.path,
      icon: card.icon || 'ℹ️',
      category: 'Information',
      priority: 2,
    };
    }),
  ];
  }),
];

export const searchSynonyms = Object.fromEntries(
  (searchSettings.synonyms || []).map(({ term, expandsTo }) => [
    (term || '').toLowerCase(),
    expandsTo,
  ])
);

export const searchCategories = [
  'All',
  'People',
  'Student Life',
  'Jewish Learning',
  'Events',
  'Parents',
  'Religious Life',
  'Travel',
  'Opportunities',
  'Services',
  'Information',
  'Alumni',
  'Facility',
];

export default searchIndex;
